import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { AuditFilterBar } from "@/components/audit/AuditFilterBar";
import { AuditTimeline } from "@/components/audit/AuditTimeline";
import { useAuditEvents } from "@/hooks/useAuditEvents";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useProjects } from "@/hooks/useProjects";
import { sinceDate } from "@/components/tokens/PeriodFilter";

/**
 * Org-level audit page (SD4), composed like CostPage: header + filter bar
 * + timeline. Unfiltered org-wide listing 403s for plans below team
 * (api/v2/audit.py) — that's an entitlement boundary, rendered as an
 * informational notice (mirrors S16430.4's graceful-degradation
 * precedent), never as a generic error.
 */
export function AuditPage() {
  const { t } = useTranslation();
  const { projects } = useProjects();

  const [eventKind, setEventKind] = useState("");
  const [rawWorkItemRef, setRawWorkItemRef] = useState("");
  const workItemRef = useDebouncedValue(rawWorkItemRef, 300);
  const [periodDays, setPeriodDays] = useState(30);
  const [projectId, setProjectId] = useState("");

  const filters = useMemo(
    () => ({
      projectId: projectId || undefined,
      eventKind: eventKind || undefined,
      workItemRef: workItemRef || undefined,
      since: sinceDate(periodDays),
    }),
    [projectId, eventKind, workItemRef, periodDays],
  );

  const { events, total, earliestEventAt, loading, error, forbidden, refetch } =
    useAuditEvents(filters);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("audit.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("audit.subtitle")}</p>
      </div>

      <AuditFilterBar
        eventKind={eventKind}
        onEventKindChange={setEventKind}
        workItemRef={rawWorkItemRef}
        onWorkItemRefChange={setRawWorkItemRef}
        periodDays={periodDays}
        onPeriodDaysChange={setPeriodDays}
        projectOptions={projects}
        projectId={projectId}
        onProjectIdChange={setProjectId}
      />

      {forbidden ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-12 text-center">
          <ShieldAlert className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm font-medium">{t("audit.planGated")}</p>
          <p className="text-xs text-muted-foreground">
            {t("audit.planGatedDetail")}
          </p>
        </div>
      ) : (
        <AuditTimeline
          events={events}
          total={total}
          earliestEventAt={earliestEventAt}
          loading={loading}
          error={error}
          onRetry={refetch}
        />
      )}
    </div>
  );
}
